import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { mcpGatewaysKeys } from "@/app/mcp-gateways/api/query-keys";
import { getCoordinatorApi } from "@/lib/api";
import { useResourceScope } from "@/lib/resource-context";
import {
  type UserSourceCreateRequest,
  type UserSourceUpdateRequest,
  userSourceCreateSchema,
} from "../types";
import { createUserSource } from "./create-user-source";
import { deleteUserSource } from "./delete-user-source";
import { userSourcesQueryOptions } from "./get-user-sources";
import { userSourcesKeys } from "./query-keys";
import { testIssuer } from "./test-issuer";
import { updateUserSource } from "./update-user-source";

export const useGetUserSources = () => {
  const scope = useResourceScope();

  return useSuspenseQuery({
    ...userSourcesQueryOptions({ scope }),
  });
};

export const useGetUserSourcesQuery = () => {
  const scope = useResourceScope();

  return useQuery({
    ...userSourcesQueryOptions({ scope }),
  });
};

export const useDeleteUserSource = (userSourceId: string) => {
  const scope = useResourceScope();
  const client = getCoordinatorApi(scope);

  return useMutation({
    mutationFn: () => deleteUserSource({ client, userSourceId }),
    meta: {
      // Gateway list rendering reads user-source status to gate the enable
      // switch (see McpGatewayActions). Invalidate it so a deleted source
      // is reflected on the gateways page without waiting for focus refetch.
      invalidates: [
        userSourcesKeys.lists({ scope }),
        mcpGatewaysKeys.lists({ scope }),
      ],
      errorMessage: "Unable to delete user source",
    },
    onSuccess: () => {
      toast.success("User source deleted");
    },
  });
};

export const useCreateUserSource = () => {
  const scope = useResourceScope();
  const client = getCoordinatorApi(scope);

  return useMutation({
    mutationFn: (payload: UserSourceCreateRequest) =>
      createUserSource({ client, payload }),
    meta: {
      invalidates: [userSourcesKeys.lists({ scope })],
      errorMessage: "Unable to create user source",
    },
    onSuccess: () => {
      toast.success("User source created");
    },
  });
};

export const useUpdateUserSource = (userSourceId: string) => {
  const scope = useResourceScope();
  const client = getCoordinatorApi(scope);

  return useMutation({
    mutationFn: (payload: UserSourceUpdateRequest) =>
      updateUserSource({ client, userSourceId, payload }),
    meta: {
      // A status change here flips the gateway-side enable guard. Invalidate
      // gateway list cache so the change reflects without waiting on focus.
      invalidates: [
        userSourcesKeys.lists({ scope }),
        mcpGatewaysKeys.lists({ scope }),
      ],
      errorMessage: "Unable to update user source",
    },
    onSuccess: () => {
      toast.success("User source updated");
    },
  });
};

/**
 * On-blur issuer pre-check, keyed on the issuer string. A query (not a mutation)
 * keeps a failed check inline only — the global error toast fires on mutations,
 * not queries — and binds each result to its issuer, so a response for a
 * since-changed value can't paint over the current one. A 422 resolves as
 * `{ ok: false }` data; anything else throws and surfaces as `isError`, which
 * callers treat as "check unavailable" and degrade silently.
 *
 * The round trip is gated on the form's own issuer field rule
 * (`userSourceCreateSchema.shape.issuer`), so the pre-check never fires for a
 * value the form itself would reject.
 */
export const useIssuerCheckQuery = (issuer: string) => {
  const scope = useResourceScope();
  const client = getCoordinatorApi(scope);

  return useQuery({
    queryKey: userSourcesKeys.issuerCheck({ scope, issuer }),
    queryFn: () => testIssuer({ client, issuer }),
    enabled: userSourceCreateSchema.shape.issuer.safeParse(issuer).success,
    retry: false,
    staleTime: 60_000,
  });
};
